import { useDisclosure } from '@chakra-ui/react';
import {
  IOneTimeInvoiceFormSchemaWithOfferings,
  IOneTimeOfferingRequestSchemaUi,
  OfferingTypesEnum,
} from '@monetize/types/app';
import { formatInteger } from '@monetize/utils/core';
import { useEffect, useMemo, useRef } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormGetValues,
} from 'react-hook-form';
import { BsTrash } from 'react-icons/bs';
import {
  MBox,
  MButton,
  MCustomIconButton,
  MFlex,
  MFormField,
  MSimpleGrid,
  MText,
} from '../../../../components/Monetize';
import { DatePicker } from '../../../../components/Monetize/DatePicker/DatePicker';
import { GRID_PROPS } from '../../../../constants/oneTimeInvoice';
import { arrayToObject } from '../../../../utils/misc';
import { isOneTimeInvoiceProductSelected } from '../oneTimeInvoice.utils';
import { OneTimeInvoiceManageProductsDrawer } from './OneTimeInvoiceManageProductsDrawer';
import { OneTimeInvoiceOfferingDropdown } from './OneTimeInvoiceOfferingDropdown';
import { OneTimeInvoiceOfferingProduct } from './OneTimeInvoiceOfferingProduct';

interface OneTimeInvoiceOfferingProps {
  control: Control<IOneTimeInvoiceFormSchemaWithOfferings>;
  getValues: UseFormGetValues<IOneTimeInvoiceFormSchemaWithOfferings>;
  index: number;
  errors: FieldErrors<IOneTimeInvoiceFormSchemaWithOfferings>;
  currency: string;
  values: IOneTimeOfferingRequestSchemaUi;
  handleRemove: (index: number) => void;
  handleUpdate: (index: number, offeringId: string) => void;
}

export const OneTimeInvoiceOffering = ({
  control,
  getValues,
  index,
  errors,
  currency,
  values,
  handleRemove,
  handleUpdate,
}: OneTimeInvoiceOfferingProps) => {
  const { fields: productFields, update: updateProduct } = useFieldArray({
    control,
    name: `offerings.${index}.products`,
  });

  const isOneTimeOfferingType =
    values.metadata.offering?.type === OfferingTypesEnum.ONETIME;
  const selectedOffering = values.metadata.offering;

  const productsById = useMemo(
    () => arrayToObject(selectedOffering?.products || [], 'id'),
    [selectedOffering],
  );

  const productsWithOptionalMandatory = useMemo(() => {
    return productFields.map((field) => ({
      ...productsById[field.productId],
      isMandatory: field.isMandatory,
      isSelected: field.isSelected,
      fieldId: field.id,
      productId: field.productId,
    }));
  }, [productFields, productsById]);

  const mandatoryProductCount = productsWithOptionalMandatory.filter(
    ({ isMandatory }) => isMandatory,
  ).length;
  const selectedProductCount = productsWithOptionalMandatory.filter(
    isOneTimeInvoiceProductSelected,
  ).length;
  const totalCount = productsWithOptionalMandatory.length;
  const isAllMandatory = mandatoryProductCount === totalCount;
  const hasNoSelectedProducts = totalCount > 0 && selectedProductCount === 0;

  const {
    open: isManageDrawerOpen,
    onOpen: openManageDrawer,
    onClose: closeManageDrawer,
  } = useDisclosure();

  /**
   * An offering whose products are all optional starts with nothing to bill, so
   * the drawer is opened for the user. This runs once per offering (the row is
   * remounted when the offering changes) so that dismissing the drawer without
   * picking anything does not immediately re-open it.
   */
  const hasAutoOpenedManageDrawer = useRef(false);
  useEffect(() => {
    if (hasNoSelectedProducts && !hasAutoOpenedManageDrawer.current) {
      hasAutoOpenedManageDrawer.current = true;
      openManageDrawer();
    }
  }, [hasNoSelectedProducts, openManageDrawer]);

  const handleSelectProduct = (productIndex: number, isSelected: boolean) => {
    // Read live form state rather than the field array snapshot, which is only
    // refreshed on array actions and would revert in-progress price edits.
    const current = getValues(`offerings.${index}.products.${productIndex}`);
    updateProduct(productIndex, { ...current, isSelected });
  };

  return (
    <>
      <MSimpleGrid {...GRID_PROPS}>
        <MBox>
          <MFormField
            error={errors?.offerings?.[index]?.offeringId}
            maxW="19rem"
          >
            <Controller
              name={`offerings.${index}.offeringId`}
              control={control}
              render={({ field: { onChange, ...rest } }) => (
                <OneTimeInvoiceOfferingDropdown
                  onChange={(value) => handleUpdate(index, value as any)}
                  {...rest}
                />
              )}
            />
          </MFormField>
        </MBox>
        <MFormField error={errors?.offerings?.[index]?.startDate} maxW="11rem">
          <Controller
            name={`offerings.${index}.startDate`}
            control={control}
            render={({ field }) => <DatePicker {...field} />}
          />
        </MFormField>
        {isOneTimeOfferingType ? (
          <MText fontStyle="italic" fontWeight="400" color="tGray.darkPurple">
            Not Applicable
          </MText>
        ) : (
          <MFormField error={errors?.offerings?.[index]?.endDate} maxW="11rem">
            <Controller
              name={`offerings.${index}.endDate`}
              control={control}
              render={({ field }) => <DatePicker {...field} clearable />}
            />
          </MFormField>
        )}

        <MFlex gridColumn="4/-1" justify="flex-end" align="center">
          <MCustomIconButton
            variant="icon"
            btnSize={4}
            containerSize={8}
            p={3}
            icon={BsTrash}
            aria-label="Remove offering"
            onClick={() => handleRemove(index)}
          />
        </MFlex>
      </MSimpleGrid>

      {selectedOffering?.description && (
        <MBox maxW="19rem" mb="2">
          <MText lineClamp={2} maxWidth="80ch">
            {selectedOffering.description}
          </MText>
        </MBox>
      )}

      {!isAllMandatory && (
        <MBox mb="2">
          <MButton variant="tertiary" onClick={openManageDrawer}>
            Manage Products {formatInteger(selectedProductCount)}/
            {formatInteger(totalCount)}
          </MButton>
        </MBox>
      )}

      {productsWithOptionalMandatory.map(
        (item, productIndex) =>
          isOneTimeInvoiceProductSelected(item) && (
            <OneTimeInvoiceOfferingProduct
              key={item.id}
              control={control}
              errors={errors}
              offeringIndex={index}
              index={productIndex}
              selectedProduct={item}
              currency={currency}
            />
          ),
      )}

      {isManageDrawerOpen && (
        <OneTimeInvoiceManageProductsDrawer
          isOpen
          onClose={closeManageDrawer}
          offeringName={selectedOffering?.name ?? 'Offering'}
          products={productsWithOptionalMandatory}
          onSelectProduct={handleSelectProduct}
        />
      )}
    </>
  );
};
